import React, { useState, useEffect, useMemo } from 'react';
import {  useTheme, useMediaQuery, Box, Grid, Typography, TextField, Button, styled, MenuItem, Chip, Alert  } from '@mui/material';
import BaseCodeSelectbox from '../../common/form/selectbox/BaseCodeSelectbox';
import { eachMonthOfInterval, endOfMonth, format, addMonths, set, lastDayOfMonth, isBefore, parseISO } from 'date-fns';

const InvoiceInfoForm = ({ formik, isLocked, renderDatePicker, titleName, prjTyp, getMobileLabel, mobileInputLabelProps, updateNot}) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   // 1. 제공해주신 커스텀 FormLabel 스타일 정의
  const FormLabel = styled(Box)(({ theme }) => ({
    ...theme.typography.subtitle2,
    textAlign: 'left',   
    paddingLeft: '30px', 
    display: 'flex',     
    alignItems: 'center',
    height: '100%',      
    fontSize: '0.875rem',
    fontWeight: 600, // 가독성을 위해 굵기 추가
  }));

  // [공통] 스케줄 계산 함수
  const getSchedule = (stDt, edDt, monOpt, dayOpt, baseDates = []) => {
    if (!stDt || !edDt || !monOpt) return [];
    try {
      const start = new Date(stDt);
      const end = new Date(edDt);
      const selectedDay = parseInt(dayOpt || '31', 10);
      
      // 기본적으로 계약 기간 내의 월들을 생성 (발행일 계산용)
      const months = eachMonthOfInterval({ start, end });
      let targetMonths = [];

      // 수금 조건 계산 시: 발행일(baseDates)이 있다면 그 날짜들을 기준으로 월을 이동
      const sourceDates = baseDates.length > 0 
        ? baseDates.map(d => parseISO(d)) 
        : months;

      switch (monOpt) {
        case '매월': 
        case '당월': 
          targetMonths = sourceDates; 
          break;
        case '익월': 
          targetMonths = sourceDates.map(d => addMonths(d, 1)); 
          break;
        case '익익월': 
          targetMonths = sourceDates.map(d => addMonths(d, 2)); 
          break;
        case '분기': 
          targetMonths = sourceDates.filter(d => (d.getMonth() + 1) % 3 === 0); 
          break;
        case '분기 익월': 
          targetMonths = sourceDates.filter(d => (d.getMonth() + 1) % 3 === 0).map(d => addMonths(d, 1)); 
          break;
        case '반기': 
          targetMonths = sourceDates.filter(d => (d.getMonth() + 1) % 6 === 0); 
          break;
        case '반기 익월': 
          targetMonths = sourceDates.filter(d => (d.getMonth() + 1) % 6 === 0).map(d => addMonths(d, 1)); 
          break;
        case '일시불': 
          targetMonths = [end]; 
          break;
        default: return [];
      }

      // 일자 보정 및 포맷팅
      return targetMonths.map(date => {
        const lastDay = lastDayOfMonth(date).getDate();
        const dayToSet = selectedDay > lastDay ? lastDay : selectedDay;
        return format(set(date, { date: dayToSet }), 'yyyy-MM-dd');
      });
    } catch (e) { return []; }
  };

  // 1. 발행 스케줄 (기준)
  const issueDatesList = useMemo(() => {
    const { contractStDt, contractEdDt, invTo, invDay } = formik.values.invoiceToBe || {};
    // 2. 필수 값들이 모두 있을 때만 스케줄 계산 실행
    if (!contractStDt || !contractEdDt) return [];
    return getSchedule(contractStDt, contractEdDt, invTo, invDay);
  }, [formik.values.invoiceToBe?.contractStDt, formik.values.invoiceToBe?.contractEdDt, formik.values.invoiceToBe?.invTo, formik.values.invoiceToBe?.invDay]);

  const collectionDatesList = useMemo(() => {
    const { contractStDt = '', contractEdDt = '', colTo, colDay } = formik.values.invoiceToBe || {};
    if (!contractStDt || !contractEdDt) return [];
    return getSchedule(contractStDt, contractEdDt, colTo, colDay, issueDatesList);
  }, [formik.values.invoiceToBe?.contractStDt, formik.values.invoiceToBe?.contractEdDt, formik.values.invoiceToBe?.colTo, formik.values.invoiceToBe?.colDay, issueDatesList]);

  useEffect(() => {
    if (formik.values.prjTyp ==='M' && formik.values.invoiceToBe?.contractStDt) { 
      if (issueDatesList && issueDatesList.length > 0) {
        const firstDate = issueDatesList[0]; 
        const startMonth = typeof firstDate === 'string' 
          ? firstDate.split('-')[1] 
          : String(new Date(firstDate).getMonth() + 1).padStart(2, '0');
        formik.setFieldValue('invoiceToBe.invMonth', startMonth);
      }
    }
  }, [issueDatesList]); 

useEffect(() => {
  if (formik.values.prjTyp ==='M' && formik.values.invoiceToBe?.contractStDt) { 
    if (collectionDatesList && collectionDatesList.length > 0) {
      // 1. 첫 번째 날짜 가져오기 (예: "2023-10-15" 또는 Date 객체)
      const firstDate = collectionDatesList[0]; 
      
      // 2. '월' 정보만 추출 (형식에 따라 slice 또는 getMonth 사용)
      // 예: "2023-10-15" 문자열인 경우 "10"만 추출
      const startMonth = typeof firstDate === 'string' 
        ? firstDate.split('-')[1] 
        : String(new Date(firstDate).getMonth() + 1).padStart(2, '0');

      // 3. Formik에 저장
      formik.setFieldValue('invoiceToBe.colMonth', startMonth);
    }
}
}, [collectionDatesList]); // 리스트가 계산될 때마다 실행

useEffect(() => {
  const { prjTyp, invoiceToBe } = formik.values;
  // 기본 조건 체크
  if (prjTyp === 'M' && invoiceToBe?.contractStDt && invoiceToBe?.contractEdDt) {
    // 리스트가 존재할 때만 로직 실행
    if (issueDatesList?.length > 0 && collectionDatesList?.length > 0) {
      const maxLength = Math.max(issueDatesList.length, collectionDatesList.length);
      const datePairs = [];

      for (let i = 0; i < maxLength; i++) {
        const issueDate = issueDatesList[i];
        const colDate = collectionDatesList[i];

        // [중요] issueDate와 colDate가 모두 존재할 때만 push
        if (issueDate && colDate) {
          datePairs.push({
            issueDate: issueDate,
            colDate: colDate
          });
        }
      }

      // 두 리스트의 쌍이 완성된 데이터만 로그에 출력 및 저장
      if (datePairs.length > 0) {
        //console.log("생성된 온전한 날짜 쌍:", datePairs);
        formik.setFieldValue('invoiceToBe.dateSchedules', datePairs);
      }
    }
  }
}, [issueDatesList, collectionDatesList]);

  // 3. 유효성 검사 (수금일 < 발행일 체크)
  const dateError = useMemo(() => {
    if (issueDatesList.length === 0 || collectionDatesList.length === 0) return "";
      const checkCount = Math.min(issueDatesList.length, collectionDatesList.length);
      for (let i = 0; i < checkCount; i++) {
        if (isBefore(parseISO(collectionDatesList[i]), parseISO(issueDatesList[i]))) {
          return `⚠️ ${i + 1}회차 수금일이 발행일보다 빠릅니다.`;
        }
      }
    return "";
  }, [issueDatesList, collectionDatesList]);

    // 5. Formik 에러 연동 (선택사항: 저장 버튼 비활성화 등을 위해)
  useEffect(() => {
    if (formik.values.prjTyp ==='M' && dateError) {
      formik.setFieldError('invoiceToBe.colDay', dateError);
    }
  }, [dateError]);

  useEffect(() => {
    if (formik.values.prjTyp === 'M') {      
      const currentInvoice = formik.values.invoiceToBe || {};
      if (!currentInvoice.invDay) {
        formik.setFieldValue('invoiceToBe.invDay', '31');
      }
      if (!currentInvoice.colDay) {
        formik.setFieldValue('invoiceToBe.colDay', '31');
      }
      if (!currentInvoice.inspectYn) {
        formik.setFieldValue('invoiceToBe.inspectYn', 'N');
      }
    }
  }, [
    formik.values.prjTyp, 
    formik.values.invoiceToBe?.invDay, 
    formik.values.invoiceToBe?.colDay, 
    formik.values.invoiceToBe?.inspectYn, 
    formik.setFieldValue
  ]);

  const finalLocked = isLocked || (updateNot || formik.values?.invoiceToBe?.lockYn === 'Y');

  if (prjTyp !== 'M') {
    return null;
  }
  
  return (
    <Grid 
      container 
      spacing={1} 
      alignItems="center" 
      sx={{
        // updateNot이 'N'일 때만 배경색 적용, 아니면 투명(transparent)
        backgroundColor: updateNot ? '#f3f3f3' : 'transparent',
        transition: 'background-color 0.3s ease', // 색상 변경 시 부드러운 효과
      }}
    >
      <Grid item xs={12}>
     
        <Grid container alignItems="center">
          <Grid item xs={isMobile ? 12 : 4}>
            <Grid container spacing={1} alignItems="center">
              {!isMobile && (
                <Grid item xs={4.4}>
                  <FormLabel sx={{ fontWeight: 'bold', color: '#333' }}>
                      계약기간
                      {formik.values.prjTyp === 'M' && (
                        <Typography variant="subtitle2" component="span"  sx={{ mx: 1, color: 'error.main' }}>
                          {'(필수)'}
                        </Typography>
                      )}
                  </FormLabel>
                </Grid>
              )}
              <Grid item xs={isMobile ? 12 : 7.6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '151px' }}>
                    {renderDatePicker(
                    'invoiceToBe.contractStDt', 
                    formik.values.invoiceToBe?.contractStDt || null, 
                    (date) => formik.setFieldValue('invoiceToBe.contractStDt', date), 
                    '100%'
                    )}
                  </Box>
                  <Typography sx={{ mx: 1, color: 'text.secondary' }}>~</Typography>
                  <Box sx={{ width: '151px' }}>
                    {renderDatePicker(
                    'invoiceToBe.contractEdDt', 
                    formik.values.invoiceToBe?.contractEdDt || null, 
                    (date) => formik.setFieldValue('invoiceToBe.contractEdDt', date), 
                    '100%'
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={isMobile ? 12 : 4}> 
            <Grid container spacing={1} alignItems="center" justifyContent={isMobile ? "flex-start" : ''}>
              {!isMobile && (
                <Grid item xs={4.52}>
                  <FormLabel sx={{ fontWeight: 'bold', color: '#333'}}>검수확인 여부
                    {formik.values.prjTyp === 'M' && (
                      <Typography variant="subtitle2" component="span"  sx={{ mx: 1, color: 'error.main' }}>
                        {'(선택)'}
                      </Typography>
                    )}
                  </FormLabel>
                </Grid>
              )}
              <Grid item xs={isMobile ? 12 : 7.4}> 
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name="invoiceToBe.inspectYn"
                    // 서버에서 Y가 내려와도 UI상에서는 S(검수 필요)가 선택된 것으로 표시
                    value={formik.values.invoiceToBe?.inspectYn === 'Y' ? 'S' : (formik.values.invoiceToBe?.inspectYn || 'N')}
                    onChange={formik.handleChange}
                    label={isMobile ? getMobileLabel('검수확인 여부', '') : ''}
                    InputLabelProps={isMobile ? mobileInputLabelProps : {}}
                    InputProps={{ readOnly: finalLocked }}
                  >
                    <MenuItem value="S">검수 필요</MenuItem>
                    <MenuItem value="N">검수 없음</MenuItem>
                  </TextField>
              </Grid>
            </Grid>
          </Grid>
          {updateNot && !!formik.values?.invoiceToBe?.lockYn &&(
            <Grid item xs={isMobile ? 12 : 4}
                    sx={{
                      pl:3,
                    }}
            >
              <Box
                sx={{
                  pl: 1,
                  p: '4px 12px',
                  border: '1px solid',
                  borderColor: 'error.light',
                  borderRadius: '4px',
                  backgroundColor: '#fff5f5',
                  alignItems: 'center',
                  minHeight: '38px',
                }}
              >
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                  }}
                >
                  * 1회 이상 계산서 발행 또는 결재 요청 진행 중으로 수정이 제한됩니다.
                </Typography>
              </Box>
            </Grid>
          )}
          {!updateNot && formik.values?.invoiceToBe?.lockYn === 'Y' && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{
                fontWeight: 'bold',
                fontSize: '0.9rem',
                lineHeight: 1.2,
                whiteSpace: 'normal',
              }}
            >
              * 매출 계산서 발행 예정 내역이 잠금 상태여서 계약 기간 및 계산서 발행 정보는
              <br/>   저장되지 않습니다.
            </Typography>
           )}
        </Grid>
        {!isMobile && (
          <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '100%' }} />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} sx={{ mt: 0.5 }}>
        <Grid container spacing={1} alignItems="center">
          {!isMobile && (
              <Grid item xs={1.5}>
                <FormLabel sx={{ paddingLeft: '30px',whiteSpace: 'nowrap', display: 'flex' }} >  
                  계산서 발행 예정일
                  {formik.values.prjTyp === 'M' && (
                    <Typography variant="subtitle2" component="span"  sx={{ mx: 1, color: 'error.main' }}>
                      {'(필수)'}
                    </Typography>
                  )}
                </FormLabel>
              </Grid>
          )}
          <Grid item xs={isMobile ? 12 : 10.5}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={isMobile ? 12 : 2.9}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                      <BaseCodeSelectbox formik={formik} filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'ISU_MON', useYn: 'Y' }} name="invoiceToBe.invTo" isLabelTop={isMobile} isLocked={finalLocked} value={formik.values.invoiceToBe?.invTo ?? ''} />
                  </Box>
                  <TextField select size="small" name="invoiceToBe.invDay" sx={{ width: '130px' }} value={formik.values.invoiceToBe?.invDay || '31'} onChange={formik.handleChange} SelectProps={{ native: true }}  disabled={finalLocked} >
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(d => <option key={d} value={String(d).padStart(2, '0')}>{d}일</option>)}
                      <option value="31">31일</option>
                  </TextField>
                </Box>
              </Grid>
              {!isMobile && issueDatesList.length > 0 && (
                <Grid item xs={7.8}>
                  <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,              
                      px: 2, 
                      py: 0.6, 
                      bgcolor: '#ffffff', 
                      borderRadius: '4px', 
                      border: '1px solid #d1d1d1',
                      backgroundColor: updateNot ? '#f3f3f3' : 'transparent', 
                      ml: 1.5,
                      minHeight: '38px'     
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: 'text.primary', 
                      fontWeight: 700,         
                      whiteSpace: 'nowrap',
                      fontSize: '13px',         
                    letterSpacing: '-0.3px'
                    }}>
                      발행 예정 ({issueDatesList.length}건)
                    </Typography>
                    <Box sx={{ width: '1px', height: '16px', bgcolor: '#e0e0e0' }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', overflow: 'hidden' }}>
                      {issueDatesList.slice(0, 4).map((date, idx) => (
                        <Chip 
                          key={idx} 
                          label={date} 
                          size="medium"          
                          variant="outlined" 
                          sx={{ 
                            fontSize: '13px',    
                            height: '26px',      
                            borderRadius: '4px', 
                            fontWeight: idx === 0 ? 700 : 500,
                            color: idx === 0 ? 'primary.main' : '#555',
                            borderColor: idx === 0 ? 'primary.main' : '#ccc',
                            bgcolor: idx === 0 ? '#f0f7ff' : 'transparent',
                            '& .MuiChip-label': { px: 1.2 } 
                          }} 
                        />
                      ))}
                      {issueDatesList.length > 4 && (
                        <Typography variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            fontWeight: 600, 
                            fontSize: '13px',
                            ml: 0.5 
                          }}>
                        외 {issueDatesList.length - 4}건
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!isMobile && (
        <Grid item xs={12}>
          <Box 
            sx={{ 
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`, 
              width: '100%' 
            }} 
          />
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 0.5 }}>
        <Grid container spacing={1}>
          {!isMobile && (
            <Grid item xs={1.5}>
              <FormLabel >수금 예정일
                {formik.values.prjTyp === 'M' && (
                  <Typography variant="subtitle2" component="span"  sx={{ mx: 1, color: 'error.main' }}>
                    {'(필수)'}
                  </Typography>
                )}
              </FormLabel>
            </Grid>
          )}
          <Grid item xs={isMobile ? 12 : 10.5}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={isMobile ? 12 : 2.9}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                      <BaseCodeSelectbox formik={formik} filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'COL_MON', useYn: 'Y' }} name="invoiceToBe.colTo" isLabelTop={isMobile} isLocked={finalLocked}/>
                  </Box>
                  <TextField select size="small" name="invoiceToBe.colDay" sx={{ width: '130px' }} value={formik.values.invoiceToBe?.colDay || '31'} onChange={formik.handleChange} SelectProps={{ native: true }} disabled={finalLocked} >
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(d => <option key={d} value={String(d).padStart(2, '0')}>{d}일</option>)}
                      <option value="31">31일</option>
                  </TextField>
                </Box>
              </Grid>
              {!isMobile && collectionDatesList.length > 0 && (
                <Grid item xs={7.8}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      px: 2, 
                      py: 0.6, 
                      bgcolor: '#ffffff', 
                      borderRadius: '4px', 
                      border: '1px solid #d1d1d1',
                      backgroundColor: updateNot ? '#f3f3f3' : 'transparent',
                      ml: 1.5,
                      minHeight: '38px'
                    }}
                  >
                    <Typography variant="body2" 
                      sx={{ 
                        color: 'text.primary', 
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        fontSize: '13px',
                        letterSpacing: '-0.3px'
                      }}
                    >
                      수금 예정 ({collectionDatesList.length}건)
                    </Typography>
                    <Box sx={{ width: '1px', height: '16px', bgcolor: '#e0e0e0' }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', overflow: 'hidden' }}>
                      {collectionDatesList.slice(0, 4).map((date, idx) => (
                        <Chip 
                          key={idx} 
                          label={date} 
                          size="medium"         
                          variant="outlined" 
                          sx={{ 
                            fontSize: '13px',
                            height: '26px',
                            borderRadius: '4px', 
                            fontWeight: idx === 0 ? 700 : 500,
                            color: idx === 0 ? 'primary.main' : '#555',
                            borderColor: idx === 0 ? 'primary.main' : '#ccc',
                            bgcolor: idx === 0 ? '#f0f7ff' : 'transparent',
                            '& .MuiChip-label': { px: 1.2 } 
                          }} 
                        />
                      ))}
                      {collectionDatesList.length > 4 && (
                        <Typography variant="body2"
                          sx={{ 
                          color: 'text.secondary', 
                          fontWeight: 600, 
                          fontSize: '13px',
                          ml: 0.5 
                          }}
                        >
                          외 {collectionDatesList.length - 4}건
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
          {dateError && (
            <Grid item xs={12} sx={{ pl: isMobile ? 0 : 1.2 + 0.1 }}> 
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, ml: isMobile ? 0 : '10px' }}>{dateError}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
      {!isMobile && (
        <Grid item xs={12}>
          <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, width: '100%' }} />
        </Grid>
      )}
    </Grid>
         
  );
};

export default InvoiceInfoForm;
